import Cookies from "universal-cookie";
import { logout } from "./logout";

const cookies = new Cookies();

/**
 * Decode a JWT payload (segment 2) WITHOUT verifying the signature — used only to read
 * standard claims like `exp` on the client. The server still verifies the signature on
 * every request; this is a UX/hygiene pre-check, not an auth decision. Returns null if the
 * value isn't a decodable 3-segment JWT (e.g. an opaque token).
 */
function decodeJwtPayload(token) {
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (b64.length % 4) b64 += "=";
        return JSON.parse(atob(b64));
    } catch {
        return null;
    }
}

/**
 * True ONLY when the token can be proven expired (has a numeric `exp` that is in the past).
 * A missing token, an undecodable token, or one without `exp` returns false (fail open) so we
 * never force a logout on an unexpected token shape — the server's 401 remains the backstop.
 */
export function isTokenExpired(token) {
    if (!token) return false;
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== "number") return false;
    return payload.exp * 1000 <= Date.now();
}

/**
 * Read the stored session token (cookie first, then localStorage — matching the existing
 * order) and hand it back only if it isn't provably expired. If it IS expired, tear the
 * session down via the app's own logout() (clears storage + routes to /app) and return null,
 * so a known-dead 24h token never fires a doomed request first.
 */
export function getValidAccessToken() {
    const token = cookies.get("accessToken") || localStorage.getItem("accessToken");
    if (token && isTokenExpired(token)) {
        logout();
        return null;
    }
    return token || null;
}
